import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateMove } from '@/api/moves'
import { movesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { MoveResponseDto } from '@/api/generated/api'

interface UpdateMoveData {
  name?: string
  description?: string
  url?: string
  thumbnailUrl?: string
  durationSeconds?: number
}

/**
 * Hook to update an existing move
 *
 * Handles:
 * - Optimistic update: immediately updates cache before API call
 * - API call to update move
 * - Cache invalidation on success
 * - Rollback on error
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const updateMove = useUpdateMove()
 *
 * const handleSave = async () => {
 *   await updateMove.mutateAsync({
 *     moveId: 'move-123',
 *     data: { name: 'Updated Name', description: 'New description' }
 *   })
 * }
 * ```
 */
export function useUpdateMove() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ moveId, data }: { moveId: string; data: UpdateMoveData }) =>
      updateMove(moveId, data),
    onMutate: async ({
      moveId,
      data,
    }: {
      moveId: string
      data: UpdateMoveData
    }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: movesKeys.detail(moveId) })

      // Snapshot previous value
      const previousMove = queryClient.getQueryData(
        movesKeys.detail(moveId)
      ) as MoveResponseDto | undefined

      // Optimistically update cache
      queryClient.setQueryData(
        movesKeys.detail(moveId),
        (old: MoveResponseDto | undefined) => ({
          ...old,
          ...data,
          updatedAt: new Date().toISOString(),
        })
      )

      // Return context with previous value
      return { previousMove, moveId }
    },
    onSuccess: (
      updatedMove: MoveResponseDto,
      variables: { moveId: string; data: UpdateMoveData }
    ) => {
      // Invalidate detail and list queries
      queryClient.invalidateQueries({
        queryKey: movesKeys.detail(variables.moveId),
      })
      queryClient.invalidateQueries({ queryKey: movesKeys.lists() })

      // Show success toast
      toast({
        title: 'Move updated',
        description: `"${updatedMove.name}" has been updated successfully.`,
      })
    },
    onError: (
      error: Error,
      _variables: { moveId: string; data: UpdateMoveData },
      context: { previousMove?: MoveResponseDto; moveId: string } | undefined
    ) => {
      // Rollback optimistic update
      if (context?.previousMove) {
        queryClient.setQueryData(
          movesKeys.detail(context.moveId),
          context.previousMove
        )
      }

      // Show error toast
      toast({
        title: 'Failed to update move',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
